import { resolveZooProductScope } from "../../product-access/productScope";
import { generateMenu, ZOO_TIERS } from "../../Menu/MenuConfig";
import { isZooFeatureEnabled } from "../../utils/zooFeatureFlags";
import { isResearchGuestProfile, RESEARCH_GUEST, RESEARCH_GUEST_ACCESS_KEYS } from "../../utils/users/researchGuest";
import { AKONGO_MENU_DESTINATIONS, FULLCARE_MENU_DESTINATIONS } from "../registry";

const normalizePath = (value) => {
  const trimmed = String(value || "")
    .trim()
    .split("?")[0]
    .replace(/^\/+/, "")
    .replace(/\/+$/, "")
    .toLowerCase();
  return trimmed ? `/${trimmed}` : "/";
};

const staticRoutePrefix = (pattern) => {
  const parts = normalizePath(pattern).split("/").filter(Boolean);
  const staticParts = parts.filter((part) => !part.startsWith(":"));
  return staticParts.length ? `/${staticParts.join("/")}` : "/";
};

export const collectLegacyMenuPaths = (menu) => {
  if (!Array.isArray(menu)) return Object.freeze([]);
  const visit = (items) => items.flatMap((item) => {
    if (!item || typeof item !== "object") return [];
    const current = typeof item.path === "string" ? [normalizePath(item.path)] : [];
    return [...current, ...visit(Array.isArray(item.submenu) ? item.submenu : [])];
  });
  return Object.freeze(visit(menu));
};

export const deriveAccessKeysFromMenu = (
  menu,
  destinations = [...FULLCARE_MENU_DESTINATIONS, ...AKONGO_MENU_DESTINATIONS]
) => {
  const paths = collectLegacyMenuPaths(menu);
  const keys = new Set();

  destinations.forEach((destination) => {
    const pattern = destination?.versions?.v1?.pattern;
    const prefix = staticRoutePrefix(pattern);
    const visible = paths.some(
      (path) => path === prefix || path.startsWith(`${prefix}/`)
    );
    if (visible) keys.add(destination.accessKey);
  });

  return keys;
};

export const createLegacyAccessKeys = ({ viewer, zooTier, userRole, zooOptions }) => {
  // Invité recherche (sans parc) : ses projets, son compte, le centre d'aide.
  // Rien d'autre, quel que soit le menu Classique ou une option de parc ; le
  // module des projets ne lui demande pas l'extension `researchProjects`.
  if ((isResearchGuestProfile(viewer) || userRole === RESEARCH_GUEST) && viewer?.akongoStaff !== true) {
    return new Set(RESEARCH_GUEST_ACCESS_KEYS);
  }
  try {
    // V2 callers pass the viewer resolved from users/{auth.uid}. Supplying the
    // fourth argument deliberately disables MenuConfig's legacy local cache.
    const menu = generateMenu(zooTier, userRole, zooOptions, viewer);
    const keys = deriveAccessKeysFromMenu(menu);
    // REPORT_MANAGEMENT is present in the tier contract and has audited V1
    // routes, but its legacy menu block is currently commented out. Preserve
    // that entitlement for the candidate V2 registry instead of mistaking the
    // missing projection for a denied capability.
    // « Bien démarrer » n'a pas d'entrée V1 d'où déduire un droit : la page
    // est ouverte à toute personne connectée et n'affiche, étape par étape,
    // que ce que ses propres droits lui permettent d'ouvrir.
    keys.add("GETTING_STARTED");
    const scope = resolveZooProductScope(zooOptions, viewer?.userId);
    if (userRole !== "pedagoadmin" && (scope.mode === "legacy" ? ZOO_TIERS[zooTier]?.features?.includes("REPORT_MANAGEMENT") : scope.can("extensions.advancedReports"))) {
      keys.add("REPORT_MANAGEMENT");
    }
    // AdminSettings is a trained direct route for Akongo super-admins but the
    // current generated legacy menu omits its standalone item. Preserve the
    // route entitlement explicitly; otherwise V2 denies a view that V1 opens.
    const isAkongoMember = viewer?.akongoStaff === true || viewer?.akongoRank === "board";
    // « Alertes » (Cahier de secteur) n'existe qu'en V2 : ses règles notifient
    // l'équipe et créent des tâches, elles se règlent donc là où l'on organise
    // les tâches, par les administrateurs du parc. L'offre est vérifiée ensuite
    // (product:ANOMALY_ALERTS → logbook.anomalyAlerts).
    if (keys.has("TASK_MANAGEMENT") && (isAkongoMember || ["admin", "superAdmin"].includes(userRole))) {
      keys.add("ANOMALY_ALERTS");
    }
    // « Projets scientifiques » n'existe qu'en V2 : tout le parc lit, l'écriture
    // est réglée par la page et les règles. Bêta activée parc par parc : sans
    // composition, le flag `researchProjects` ; avec composition, l'offre
    // (product:SCIENTIFIC_PROJECTS → extensions.researchProjects).
    if (userRole !== "pedagoadmin" && isZooFeatureEnabled(zooOptions, "researchProjects")) {
      keys.add("SCIENTIFIC_PROJECTS");
    }
    // CRM de l'équipe : page propre à la V2, même périmètre que les règles
    // Firestore de la racine `crm` (isAkongoMember : staff ou board).
    if (isAkongoMember) keys.add("AKONGO_CRM");
    // Registre des comportements (éthogrammes V2) : page propre à la V2, réservée au
    // staff comme les règles Firestore de `behaviorRegistry` / `ethograms` (isAkongoStaff).
    if (viewer?.akongoStaff === true) keys.add("ETHOGRAM_REGISTRY");
    if (
      isAkongoMember
      || (
        viewer?.zooId === "akongo"
        && userRole === "superAdmin"
        && ZOO_TIERS[zooTier]?.features?.includes("ADMIN_SETTINGS")
      )
    ) {
      keys.add("ADMIN_SETTINGS");
      keys.add("AKONGO_EVALUATION_HISTORY");
    }
    // Tableau de bord « Bien démarrer » (V2 seule) : le staff AKONGO, et lui seul,
    // comme la lecture Firestore de la progression et des avis (`isAkongoStaff()`).
    if (viewer?.akongoStaff === true) keys.add("ONBOARDING_ANALYTICS");
    // Relecture de la base commune des médicaments (V2 seule) : le staff AKONGO
    // seul, comme l'écriture Firestore de la base (`isAkongoStaff()`).
    if (viewer?.akongoStaff === true) keys.add("PHARMACY_COMMUNITY_REVIEW");
    // Base commune des aliments (V2 seule) : le staff AKONGO seul, comme
    // l'écriture Firestore de `foodCatalog` (`isAkongoStaff()`).
    if (viewer?.akongoStaff === true) keys.add("FOOD_CATALOG_REVIEW");
    return keys;
  } catch (_) {
    return new Set();
  }
};
